import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { signOut, supabase } from '../../utils/supabase';
import PlanBadge from '../common/PlanBadge';
import { planService } from '../../services/planService';
import {
  HomeIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ArrowLeftOnRectangleIcon,
  DocumentIcon,
  BellIcon,
  ShoppingBagIcon,
  TruckIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  CalculatorIcon,
  BuildingStorefrontIcon,
  UserCircleIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/app/dashboard', icon: HomeIcon },
  { name: 'Commandes', href: '/app/orders', icon: ShoppingBagIcon },
  { name: 'Produits', href: '/app/products', icon: ShoppingBagIcon },
  { name: 'Livreurs', href: '/app/delivery-persons', icon: TruckIcon },
  { name: 'Attribution commandes', href: '/app/delivery-persons-orders', icon: ClipboardDocumentCheckIcon },
  { name: 'Clients', href: '/app/clients', icon: UserGroupIcon },
  { name: 'Clientes Bloquées', href: '/app/unresponsive-customers', icon: ChatBubbleLeftRightIcon },
  { name: 'Notifications', href: '/app/notifications', icon: BellIcon },
  { name: 'Caissiers', href: '/app/cashiers', icon: CalculatorIcon },
  { name: 'Boutiques', href: '/app/shops', icon: BuildingStorefrontIcon },
  { name: 'Configuration', href: '/app/settings', icon: Cog6ToothIcon },
  { name: 'Templates', href: '/app/templates', icon: DocumentIcon },
  { name: 'Analytiques', href: '/app/analytics', icon: ChartBarIcon },
  { name: 'Profil', href: '/app/profile', icon: UserCircleIcon },
];

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [menuAccess, setMenuAccess] = useState({});
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });

  // Récupérer les informations utilisateur
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Récupérer le nom depuis user_settings
        const { data: userSettings } = await supabase
          .from('user_settings')
          .select('full_name')
          .eq('user_id', user.id)
          .single();

        setUserInfo({
          name: userSettings?.full_name || user.user_metadata?.full_name || '',
          email: user.email || ''
        });
      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur:', error);
      }
    };

    fetchUserInfo();
  }, []);

  // Vérifier les accès pour tous les éléments de menu
  useEffect(() => {
    const checkMenuAccess = async () => {
      const accessMap = {};
      
      // Vérifier l'accès pour chaque élément de navigation
      for (const item of navigation) {
        try {
          const hasAccess = await planService.canAccessRoute(item.href);
          accessMap[item.href] = hasAccess;
        } catch (error) {
          console.error(`Erreur lors de la vérification d'accès pour ${item.href}:`, error);
          accessMap[item.href] = true; // Par défaut, autoriser en cas d'erreur
        }
      }
      
      setMenuAccess(accessMap);
    };

    checkMenuAccess();
  }, []);

  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      if (error) throw error;
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Fonction pour rendre un élément de menu mobile avec gestion des accès
  const renderMobileMenuItem = (item, isActive) => {
    const hasAccess = menuAccess[item.href];
    
    // Si l'accès n'est pas encore vérifié, afficher l'élément normalement
    if (hasAccess === undefined) {
      return (
        <Link
          key={item.name}
          to={item.href}
          className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${
            isActive
              ? 'bg-gray-900 text-white'
              : 'text-gray-300 hover:bg-gray-700 hover:text-white'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <item.icon
            className={`mr-4 h-6 w-6 flex-shrink-0 ${
              isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'
            }`}
          />
          {item.name}
        </Link>
      );
    }

    // Si l'utilisateur n'a pas accès, afficher en désactivé avec icône de verrouillage
    if (!hasAccess) {
      return (
        <div
          key={item.name}
          className="group flex items-center px-2 py-2 text-base font-medium rounded-md text-gray-500 cursor-not-allowed opacity-60"
          title={`${item.name} - Nécessite un plan supérieur`}
        >
          <LockClosedIcon className="mr-4 h-6 w-6 flex-shrink-0 text-gray-500" />
          {item.name}
          <span className="ml-auto text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded-full">
            Premium
          </span>
        </div>
      );
    }

    // Si l'utilisateur a accès, afficher normalement
    return (
      <Link
        key={item.name}
        to={item.href}
        className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${
          isActive
            ? 'bg-gray-900 text-white'
            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <item.icon
          className={`mr-4 h-6 w-6 flex-shrink-0 ${
            isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'
          }`}
        />
        {item.name}
      </Link>
    );
  };

  // Fonction pour rendre un élément de menu avec gestion des accès
  const renderMenuItem = (item, isActive) => {
    const hasAccess = menuAccess[item.href];
    
    // Si l'accès n'est pas encore vérifié, afficher l'élément normalement
    if (hasAccess === undefined) {
      return (
        <Link
          key={item.name}
          to={item.href}
          className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
            isActive
              ? 'bg-gray-900 text-white'
              : 'text-gray-300 hover:bg-gray-700 hover:text-white'
          }`}
        >
          <item.icon
            className={`mr-3 h-6 w-6 flex-shrink-0 ${
              isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'
            }`}
          />
          {item.name}
        </Link>
      );
    }

    // Si l'utilisateur n'a pas accès, afficher en désactivé avec icône de verrouillage
    if (!hasAccess) {
      return (
        <div
          key={item.name}
          className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-500 cursor-not-allowed opacity-60"
          title={`${item.name} - Nécessite un plan supérieur`}
        >
          <LockClosedIcon className="mr-3 h-6 w-6 flex-shrink-0 text-gray-500" />
          {item.name}
          <span className="ml-auto text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded-full">
            Premium
          </span>
        </div>
      );
    }

    // Si l'utilisateur a accès, afficher normalement
    return (
      <Link
        key={item.name}
        to={item.href}
        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
          isActive
            ? 'bg-gray-900 text-white'
            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }`}
      >
        <item.icon
          className={`mr-3 h-6 w-6 flex-shrink-0 ${
            isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'
          }`}
        />
        {item.name}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar for desktop */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-gray-800">
          <div className="flex flex-1 flex-col  pt-5 pb-4">
            <div className="flex flex-shrink-0 items-center px-4">
              <h1 className="text-xl font-bold text-white">Mercuri Shop</h1>
            </div>
            
            {/* Informations utilisateur - Desktop */}
            <div className="px-4 mt-3">
              <div className="text-sm text-gray-300 mb-2">
                <div className="font-medium text-white truncate">
                  {userInfo.name || userInfo.email}
                </div>
                {userInfo.name && (
                  <div className="text-xs text-gray-400 truncate">
                    {userInfo.email}
                  </div>
                )}
              </div>
            </div>
            
            {/* Badge du plan - Desktop */}
            <div className="px-4 mb-2">
              <PlanBadge showProgress={true} size="small" />
            </div>
            
            <nav className="mt-5 flex-1 space-y-1 px-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return renderMenuItem(item, isActive);
              })}
            </nav>
          </div>
          <div className="flex flex-shrink-0 bg-gray-700 p-4">
            <button
              onClick={handleSignOut}
              className="group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
            >
              <ArrowLeftOnRectangleIcon className="mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-300" />
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className="md:hidden">
        <div className="fixed inset-0 z-40 flex">
          <div
            className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ease-linear duration-300 ${
              isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          />

          <div
            className={`relative flex w-full max-w-xs flex-1 flex-col bg-gray-800 pt-5 pb-4 transform transition ease-in-out duration-300 ${
              isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="sr-only">Fermer le menu</span>
                <svg
                  className="h-6 w-6 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-shrink-0 flex items-center px-4">
              <h1 className="text-xl font-bold text-white">Mercuri Shop</h1>
            </div>
            
            {/* Informations utilisateur - Mobile */}
            <div className="px-4 mt-3">
              <div className="text-sm text-gray-300 mb-2">
                <div className="font-medium text-white truncate">
                  {userInfo.name || userInfo.email}
                </div>
                {userInfo.name && (
                  <div className="text-xs text-gray-400 truncate">
                    {userInfo.email}
                  </div>
                )}
              </div>
            </div>
            
            {/* Badge du plan - Mobile */}
            <div className="px-4 mb-2">
              <PlanBadge showProgress={true} size="small" />
            </div>
            
            <div className="mt-5 flex-1 h-0 overflow-y-auto">
              <nav className="px-2 space-y-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return renderMobileMenuItem(item, isActive);
                })}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Header avec badge du plan pour mobile */}
        <div className="sticky top-0 z-10 md:hidden bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <span className="sr-only">Ouvrir le menu</span>
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            
            {/* Informations utilisateur et plan - Header mobile */}
            <div className="flex flex-col items-end">
              <div className="text-sm font-medium text-gray-900 truncate max-w-32">
                {userInfo.name || userInfo.email}
              </div>
              <PlanBadge showProgress={false} size="small" />
            </div>
          </div>
        </div>
        
        <main className="flex-1">
          <div className="">
            <div className="mx-auto">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
